import React, { ReactElement, useEffect, useState } from 'react';

import { formatUsername, getClassNames, noOp } from '../../../../../utils/util';
import { APPROVED, ARCHIVED, DECLINED, LONG_MONTHS, PENDING } from '../../../../../utils/constants';

import { Comment, HashTag, Post, PostWithHierarchy, Reaction } from '../../../../../types/entity.types';

import TribeInputContainer from '../../../../../containers/programme/tribe/tribe-input/TribeInput.container';
import TribeFilesContainer from '../../../../../containers/programme/tribe/tribe-files/TribeFiles.container';
import TribeCommentContainer from '../../../../../containers/programme/tribe/tribe-comment/TribeComment.container';
import TribePollContainer from '../../../../../containers/programme/tribe/tribe-poll/TribePoll.container';

import TribeApprovalButton from '../tribe-approval-button/TribeApprovalButton';
import ProfilePicture from '../../../../common/profile-picture/ProfilePicture';
import TribeThumbnails from '../tribe-thumbnails/TribeThumbnails';

import { ReactComponent as PinnedIcon } from '../../../../../resources/icons/pinned.svg';
import { ReactComponent as UnpinnedIcon } from '../../../../../resources/icons/unpinned.svg';
import { ReactComponent as DotsIcon } from '../../../../../resources/icons/three-dots.svg';
import { ReactComponent as LikeIcon } from '../../../../../resources/icons/like.svg';

import './tribe-post.scss';
import { FileType } from '../../../../../types/common.types';

type Props = {
  status: string;
  post: Post;
  hashtags?: HashTag[];
  files?: FileType[];
  profilePicture?: string;
  liked?: boolean;
  comments?: Comment[];
  approval?: boolean;
  showAllComments?: boolean;
  setShowAllComments: (value: ((prevState: boolean) => boolean) | boolean) => void;
  pinPost: () => void;
  editPost: () => void;
  deletePost: () => void;
  createReaction: (value: string) => void;
  deleteReaction: (value: string) => void;
  refreshPost?: (value: string) => void;
  updatePostStatus: (status: string, callbackFn: () => void) => void;
  evaluateSupportConversation: (value: string) => void;
  hierarchy?: boolean;
  navigateToProfile?: (value: string) => void;
};

const TribePost = ({
  status,
  post,
  hashtags = [],
  files = [],
  profilePicture = '',
  liked = false,
  comments = [],
  approval = false,
  showAllComments = false,
  setShowAllComments,
  pinPost,
  editPost,
  deletePost,
  createReaction,
  deleteReaction,
  refreshPost,
  updatePostStatus,
  evaluateSupportConversation,
  hierarchy = false,
  navigateToProfile = noOp
}: Props): ReactElement => {
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [approvalStatus, setApprovalStatus] = useState<string>(post?.status ?? '');

  const {
    externalReference = '',
    text,
    startDateTime,
    pinned,
    userName,
    totalComments = 0,
    reactionCount = [],
    awaitingApproval,
    systemUserExternalReference = '',
    question,
    options
  }: Post = post;

  const [noOfComments, setNoOfComments] = useState<number>(totalComments);

  useEffect((): void => {
    if (!totalComments) {
      setNoOfComments(comments.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post, comments]);

  const totalLikes: number = reactionCount.find((reaction: Reaction): boolean => reaction.text === 'like')?.count || 0;

  const formatDate = (date: string | Date): string => {
    const dateToFormat: Date = new Date(date);

    const day: number = dateToFormat.getDate();
    const month: string | undefined = LONG_MONTHS[dateToFormat.getMonth()];
    let hour: number | string = dateToFormat.getHours();
    let minute: number | string = dateToFormat.getMinutes();

    if (hour < 10) {
      hour = '0' + hour;
    }

    if (minute < 10) {
      minute = '0' + minute;
    }

    return `${day} ${month} at ${hour}:${minute}`;
  };

  const commentNeedsApproval = (comment: Comment): boolean => {
    if (comment.awaitingApproval) {
      return true;
    }

    if (!comment.comments) {
      return false;
    }

    for (let i: number = 0; i < comment.comments.length; i++) {
      const listedComment: Comment | undefined = comment.comments[i];

      if (!listedComment) {
        continue;
      }

      if (commentNeedsApproval(listedComment)) {
        return true;
      }
    }

    return false;
  };

  const handleLikeClick = (): void => {
    if (liked) {
      deleteReaction('like');
    } else {
      createReaction('like');
    }
  };

  const handleEditClick = (): void => {
    editPost();
    setShowOptions(false);
  };

  const handleDeleteClick = (): void => {
    deletePost();
    setShowOptions(false);
  };

  const handleApprovalButtonClick = (status: string): void => {
    updatePostStatus(status, (): void => setApprovalStatus(status));
  };

  return (
    <div
      className={getClassNames('tribe-post', { approval: approval && awaitingApproval && approvalStatus === PENDING })}
      data-testid="tribe-post"
    >
      <div className="tribe-post__header-container">
        <ProfilePicture src={profilePicture} alt={userName} small />
        <div className="tribe-post__header-container__info">
          <button
            className="tribe-post__header-container__heading"
            onClick={(): void => navigateToProfile(userName as string)}
          >
            {formatUsername(userName)}
          </button>
          <p className="tribe-post__header-container__sub-heading">{formatDate(startDateTime ?? '')}</p>
        </div>
        <div
          className={getClassNames('tribe-post__header-container__icon-container', {
            disabled: status === ARCHIVED
          })}
        >
          {pinned && !approval && <PinnedIcon onClick={pinPost} data-testid="pinned-icon" />}
          {!pinned && !approval && <UnpinnedIcon onClick={pinPost} data-testid="unpinned-icon" />}
          {status !== ARCHIVED && !approval && (
            <DotsIcon
              onClick={(): void => setShowOptions((prevState: boolean): boolean => !prevState)}
              data-testid="options-icon"
            />
          )}
          {showOptions && (
            <div className="tribe-post__header-container__options">
              <p onClick={handleEditClick}>Edit Post</p>
              <p onClick={handleDeleteClick}>Delete Post</p>
            </div>
          )}
        </div>
      </div>

      <p className="tribe-post__text">{text}</p>
      {question && options && <TribePollContainer post={post} />}
      <TribeThumbnails files={files} post />
      <TribeFilesContainer files={files} post />

      {approvalStatus && (
        <div className="tribe-post__approval-actions">
          {awaitingApproval && approvalStatus === PENDING && (
            <TribeApprovalButton text="APPROVE" onClick={(): void => handleApprovalButtonClick(APPROVED)} />
          )}
          {awaitingApproval && approvalStatus === PENDING && (
            <TribeApprovalButton text="DECLINE" onClick={(): void => handleApprovalButtonClick(DECLINED)} />
          )}
          {awaitingApproval && approvalStatus === APPROVED && (
            <TribeApprovalButton text="APPROVED" onClick={(): void => handleApprovalButtonClick(PENDING)} approved />
          )}
          {awaitingApproval && approvalStatus === DECLINED && (
            <TribeApprovalButton
              text="CONTACT"
              onClick={(): void => evaluateSupportConversation(systemUserExternalReference)}
            />
          )}
          {awaitingApproval && approvalStatus === DECLINED && (
            <TribeApprovalButton text="DECLINED" onClick={(): void => handleApprovalButtonClick(PENDING)} declined />
          )}
        </div>
      )}

      {!approvalStatus && (
        <div className="tribe-post__engagement">
          <div className="tribe-post__engagement__likes">
            <LikeIcon />
            <p>{totalLikes}</p>
          </div>
          <p className="tribe-post__engagement__comments">
            {noOfComments} {noOfComments === 1 ? 'Comment' : 'Comments'}
          </p>
        </div>
      )}

      {status !== ARCHIVED && !approval && (
        <div className="tribe-post__reaction-bar" data-testid="tribe-post-reaction-bar">
          <LikeIcon
            className={getClassNames('tribe-post__reaction-bar__like-icon', { liked })}
            onClick={handleLikeClick}
            data-testid="tribe-post-like-icon"
          />
        </div>
      )}

      {status !== ARCHIVED && !approval && (
        <TribeInputContainer
          placeholder="Comment..."
          hashtags={hashtags}
          parentExternalReference={externalReference}
          onPostSuccess={(): void => {
            refreshPost?.(externalReference);
          }}
          comment
        />
      )}
      {((status === ARCHIVED && comments.length > 0) || (approval && comments.length > 0)) && (
        <hr className="tribe-post__divider" />
      )}

      {!approval && (
        <div className="tribe-post__comments">
          {(comments ?? []).map((comment: Comment, index: number): ReactElement => {
            if (showAllComments || hierarchy || index < 2) {
              return (
                <TribeCommentContainer
                  key={comment.externalReference}
                  status={status}
                  comment={comment}
                  hashtags={hashtags}
                  showReplies={showAllComments}
                  postExternalReference={externalReference}
                  refreshPost={refreshPost}
                  hierarchyComments={(post as PostWithHierarchy).hierarchyComments}
                  hierarchy={hierarchy}
                />
              );
            }

            return <React.Fragment key={index} />;
          })}
        </div>
      )}

      {approval && (
        <div className="tribe-post__comments">
          {comments?.map((comment: Comment): ReactElement => {
            if (commentNeedsApproval(comment)) {
              return (
                <TribeCommentContainer
                  key={comment.externalReference}
                  status={status}
                  comment={comment}
                  showReplies={true}
                  approval
                />
              );
            }

            return <React.Fragment key={comment.externalReference} />;
          })}
        </div>
      )}

      {totalComments > 2 && !approval && (
        <p
          className="tribe-post__comments-action"
          onClick={(): void => setShowAllComments((prevState: boolean): boolean => !prevState)}
        >
          View {showAllComments ? 'Less' : 'All'} Comments
        </p>
      )}
    </div>
  );
};

export default TribePost;
